import loadable from "@loadable/component"
import { createSelector } from "@reduxjs/toolkit"
import PropTypes from "prop-types"
import { useEffect, useMemo, useRef, useState } from "react"
import { shallowEqual, useSelector } from "react-redux"

import { makeShouldUseNewWizardExp } from "areas/product/jewlr"
import SimpleDeferAfterLoadWrapper from "components/common/simple-defer-load-wrapper"
import Expand from "components/ui/expand"
import { retry } from "helpers/application"
import { cn } from "styles/utils"

const AccentOptions = loadable(() => retry(() => import("./accent-options")), {
  ssr: false,
})
const AdditionalMetalOptions = loadable(
  () => retry(() => import("./additional-metal-options")),
  { ssr: false }
)
const AdditionsOptions = loadable(
  () => retry(() => import("./additions-options")),
  { ssr: false }
)
const BandOptions = loadable(() => retry(() => import("./band-options")), {
  ssr: false,
})
const BoxOptions = loadable(() => retry(() => import("./box-options")), {
  ssr: false,
})
const BoxDesignModal = loadable(
  () => retry(() => import("areas/product/jewlr/modals/box_design_modal")),
  { ssr: false }
)
const BuilderOptions = loadable(
  () => retry(() => import("./builder-options")),
  { ssr: false }
)
// const BuilderItem = loadable(() => retry(() => import("./builder-item")),{ ssr: false })
const CageHeadOptions = loadable(
  () => retry(() => import("./cage-head-options")),
  { ssr: false }
)
const CageShankOptions = loadable(
  () => retry(() => import("./cage-shank-options")),
  { ssr: false }
)
const ChainOptions = loadable(() => retry(() => import("./chain-options")), {
  ssr: false,
})
// const WarrantyOption = loadable(() => retry(() => import("./warranty-option")),{ ssr: false })
const ChainSizeHalfModal = loadable(
  () => retry(() => import("areas/product/jewlr/modals/chain_size_half_modal")),
  { ssr: false }
)
const ChoiceOptions = loadable(() => retry(() => import("./choice-options")), {
  ssr: false,
})
const DiamondSelector = loadable(
  () => retry(() => import("areas/product/diamond-selector")),
  { ssr: false }
)

const Engravings = loadable(
  () => retry(() => import("areas/product/wizard-options/engravings")),
  { ssr: false }
)
const EngravingsHalfModal = loadable(
  () => retry(() => import("areas/product/jewlr/modals/engravings_half_modal")),
  { ssr: false }
)

const FaceOptions = loadable(() => retry(() => import("./face-options")), {
  ssr: false,
})
const ImageWizardOptions = loadable(
  () => retry(() => import("./image-wizard-options")),
  { ssr: false }
)
const PhotoUploadModal = loadable(
  () => retry(() => import("areas/product/jewlr/modals/photo_upload_modal")),
  { ssr: false }
)
const RingWidthOptions = loadable(
  () => retry(() => import("./ring-width-options")),
  { ssr: false }
)
const RingSizeHalfModal = loadable(
  () => retry(() => import("areas/product/jewlr/modals/ring_size_half_modal")),
  { ssr: false }
)
const ShankOptions = loadable(() => retry(() => import("./shank-options")), {
  ssr: false,
})
const SizeOptions = loadable(() => retry(() => import("./size-options")), {
  ssr: false,
})
const SkuOptions = loadable(() => retry(() => import("./sku-options")), {
  // ssr: false,
})
const StoneCount = loadable(() => retry(() => import("./stone-count")), {
  ssr: false,
})
const StoneOptions = loadable(() => retry(() => import("./stone-options")), {
  ssr: false,
})
const SwitchOptionsModal = loadable(
  () =>
    retry(() => import("areas/product/jewlr/modals/switch_options_half_modal")),
  { ssr: false }
)

const selectAdminMode = (state) => state.wizard.adminMode
const selectOlid = (state) => state.wizard.olid
const selectOpenedPanelIndexes = (state) => state.panel.openedPanelIndexes
const selectPrefStore = (state) => state.wizardUI.prefStore
const selectSubOpenedPanelIndex = (state) => state.panel.subOpenedPanelIndex
const selectTags = (state) => state.wizard.tags

const makeSelectAccordionContentData = (panel, prefOf) => {
  const selectWizardData = (state) => state.wizard.data[panel]

  return createSelector(
    [
      selectAdminMode,
      selectOlid,
      selectOpenedPanelIndexes,
      selectPrefStore,
      selectSubOpenedPanelIndex,
      selectTags,
      selectWizardData,
    ],
    (
      adminMode,
      olid,
      openedPanelIndexes,
      prefStore,
      subOpenedPanelIndex,
      tags,
      wizardData
    ) => {
      let overrideText = wizardData?.override_text
        ? "" + wizardData.override_text
        : null

      const currentOpenedPanelIndexes = prefOf
        ? subOpenedPanelIndex[prefOf] || ""
        : openedPanelIndexes

      return {
        adminMode,
        forHer: tags.includes("for-her"),
        forHim: tags.includes("for-him"),
        olid,
        openPanel: currentOpenedPanelIndexes.includes(panel),
        overrideText,
        prefStore,
      }
    }
  )
}

const AccordionContent = ({ index, panel, pref, prefOf }) => {
  const panelRef = useRef()
  const [openedPanel, setOpenedPanel] = useState(null)

  const selectAccordionContentData = useMemo(
    () => makeSelectAccordionContentData(panel, prefOf),
    [panel, prefOf]
  )
  const shouldUseNewWizardExp = makeShouldUseNewWizardExp()

  const {
    adminMode,
    forHer,
    forHim,
    olid,
    openPanel,
    overrideText,
    prefStore,
  } = useSelector(selectAccordionContentData, shallowEqual)

  useEffect(() => {
    if (!openedPanel && openPanel && panelRef.current) {
      const panel_element = panelRef.current
      if (panel_element) {
        setOpenedPanel(panel)

        // Scroll selected item into view
        const selectedItem = panel_element.querySelectorAll(
          "[data-is-selected='true'][data-is-option='true']"
        )[0]
        if (selectedItem) {
          const hasScrollable = selectedItem.parentNode.dataset.slider
          if (hasScrollable) {
            const scrollable = selectedItem.parentNode
            let distance = selectedItem.offsetLeft - selectedItem.clientWidth
            distance = Math.max(
              0,
              Math.min(
                distance,
                scrollable.scrollWidth - scrollable.clientWidth
              )
            )
            scrollable.scrollLeft = distance
          }
        }
      }
    } else if (openedPanel === panel && !openPanel) {
      setOpenedPanel(false)
    }
  }, [openPanel, openedPanel])

  if (!prefStore && !(shouldUseNewWizardExp && pref === "sku" && index === 0)) {
    return null
  }

  let content = null
  let wizardModal = null
  const extraOut = []

  switch (pref) {
    case "cc":
      content = (
        <div>
          {extraOut.map((extra) => (
            <div key={`extracc-${extra}`}>{extra}</div>
          ))}
          <BuilderOptions pref={pref} />
        </div>
      )
      break
    case "sku":
      content = <SkuOptions pref={pref} />
      break
    case "as":
      content = <AccentOptions pref={pref} />
      break
    case "ef":
    case "ef2":
      wizardModal = (
        <>
          <EngravingsHalfModal pref={pref} />
        </>
      )
      content = <Engravings adminMode={adminMode} pref={pref} />
      break
    case "sz":
      wizardModal = <RingSizeHalfModal />
      content = <SizeOptions pref={pref} sizePref={{ forHer, forHim }} />
      break
    case "ch":
      wizardModal = <ChainSizeHalfModal pref={pref} />
      content = <ChainOptions pref={pref} />
      break
    // case "warranty":
    //   content = <WarrantyOption pref={pref} />
    //   break
    case "rw":
      // e.g. JWM0201
      content = <RingWidthOptions />
      break
    case "sc":
      // e.d. JWL0402
      content = <StoneCount />
      break
    case "shank":
      content = <ShankOptions />
      break
    case "chead":
      // e.g. JINT0001
      content = <CageHeadOptions pref={pref} />
      break
    case "cshank":
      content = <CageShankOptions />
      break
    case "diamond":
      if (olid && !adminMode) {
        content = null
      }
      content = <DiamondSelector />
      break
    case "box":
      wizardModal = (
        <>
          <PhotoUploadModal pref="box" />
          <BoxDesignModal />
        </>
      )
      content = <BoxOptions />
    // fallthrough
    default:
      if (/^s[0-9]+/.test(pref) || /^face[0-9]+_s[0-9]+$/.test(pref)) {
        content = <StoneOptions pref={pref} />
      } else if (/^face[0-9]+$/.test(pref)) {
        content = <FaceOptions pref={pref} />
      } else if (/a[0-9]+/.test(pref)) {
        content = <AdditionsOptions pref={pref} />
      } else if (/o[0-9]+/.test(pref)) {
        wizardModal = <SwitchOptionsModal />
        content = <ChoiceOptions pref={pref} />
      } else if (/img[0-9]+/.test(pref)) {
        // this is prevent product (eg. JWLF0009) only contain one PhotoUploadModal
        wizardModal = <PhotoUploadModal pref={pref} />
        content = <ImageWizardOptions adminMode={adminMode} pref={pref} />
      } else if (/b[0-9]+/.test(pref)) {
        if (olid && !adminMode) {
          content = null
        }
        // e.g. JCOL0001
        content = <BandOptions pref={pref} />
      } else if (/m[0-9]+/.test(pref)) {
        if (olid && !adminMode) {
          content = null
        }
        // e.g. JLUX01-O1AAA
        content = <AdditionalMetalOptions pref={pref} />
      } else if (/^ch[0-9]+/.test(pref)) {
        wizardModal = <ChainSizeHalfModal pref={pref} />
        content = <ChainOptions pref={pref} />
      }
      break
  }

  return (
    <>
      <SimpleDeferAfterLoadWrapper>{wizardModal}</SimpleDeferAfterLoadWrapper>
      <Expand
        aria-labelledby={`wizard-accordion-heading-${pref}`}
        className={cn(!shouldUseNewWizardExp && "pt-10")}
        id={`wizard-accordion-content-${pref}`}
        key={`panel${pref}`}
        ref={panelRef}
        role="region"
        show={openPanel}
      >
        {overrideText ? (
          <div
            className="pl-15 my-8 ml-5 mr-8 pb-8 pr-8"
            dangerouslySetInnerHTML={{ __html: overrideText }}
          />
        ) : (
          content
        )}
      </Expand>
    </>
  )
}

AccordionContent.propTypes = {
  index: PropTypes.number,
  panel: PropTypes.string,
  pref: PropTypes.string,
  prefOf: PropTypes.string,
}

export default AccordionContent
